/*!
 * SkyPlus Warehouse Dashboard Component
 * Custom component for warehouse and inventory management
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';


// Interfaces

interface WarehouseStats {
  totalItems: number;
  totalValue: number;
  lowStockItems: number;
  pendingOrders: number;
  itemsShippedToday: number;
  itemsReceivedToday: number;
}

interface InventoryCategory {
  name: string;
  count: number;
  value: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
}

interface RecentActivity {
  id: string;
  type: 'inbound' | 'outbound' | 'adjustment' | 'transfer';
  description: string;
  quantity: number;
  timestamp: Date;
  user: string;
  status: 'completed' | 'pending' | 'cancelled';
}

interface WarehouseLocation {
  id: string;
  name: string;
  capacity: number;
  used: number;
  items: number;
}

interface LowStockAlert {
  itemCode: string;
  itemName: string;
  currentStock: number;
  reorderLevel: number;
  supplier: string;
}

@Component({
  selector: 'skyplus-warehouse',
  templateUrl: './skyplus-warehouse.component.html',
  styleUrls: ['./skyplus-warehouse.component.scss']
})
export class SkyPlusWarehouseComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // State
  isLoading = true;
  errorMessage = '';
  lastUpdated: Date = new Date();

  // Stats
  stats: WarehouseStats = {
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    pendingOrders: 0,
    itemsShippedToday: 0,
    itemsReceivedToday: 0
  };

  // Data
  categories: InventoryCategory[] = [];
  recentActivities: RecentActivity[] = [];
  warehouseLocations: WarehouseLocation[] = [];
  lowStockAlerts: LowStockAlert[] = [];
  inventoryTrendData: any[] = [];

  // Filters
  selectedTimeRange = '7d';
  selectedWarehouse = 'all';

  timeRanges = [
    { value: '1d', label: 'Today' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  warehouses = [
    { value: 'all', label: 'All Warehouses' },
    { value: 'main', label: 'Main Warehouse' },
    { value: 'secondary', label: 'Secondary Warehouse' },
    { value: 'cold-storage', label: 'Cold Storage' }
  ];

  constructor() {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }



  loadDashboardData(): void {
    this.isLoading = true;
    this.errorMessage = '';

    // Simulating API delay - Replace with actual API calls
    setTimeout(() => {
      this.loadMockData();
      this.isLoading = false;
      this.lastUpdated = new Date();
    }, 800);
  }

  private loadMockData(): void {
    // Mock Stats
    this.stats = {
      totalItems: 15847,
      totalValue: 24567890,
      lowStockItems: 23,
      pendingOrders: 156,
      itemsShippedToday: 342,
      itemsReceivedToday: 187
    };

    // Mock Categories
    this.categories = [
      { name: 'Electronics', count: 4521, value: 12500000, percentage: 28.6, trend: 'up' },
      { name: 'Office Supplies', count: 3890, value: 2340000, percentage: 24.5, trend: 'stable' },
      { name: 'Furniture', count: 1256, value: 5670000, percentage: 7.9, trend: 'down' },
      { name: 'IT Equipment', count: 2340, value: 8900000, percentage: 14.8, trend: 'up' },
      { name: 'Consumables', count: 3840, value: 1200000, percentage: 24.2, trend: 'stable' }
    ];

    // Mock Recent Activities
    this.recentActivities = [
      {
        id: 'ACT-001', type: 'inbound', description: 'Received shipment from TechCorp Ltd',
        quantity: 250, timestamp: new Date(Date.now() - 30 * 60000), user: 'John Kimani', status: 'completed'
      },
      {
        id: 'ACT-002', type: 'outbound', description: 'Order #ORD-2026-0142 dispatched',
        quantity: 45, timestamp: new Date(Date.now() - 60 * 60000), user: 'Mary Wanjiku', status: 'completed'
      },
      {
        id: 'ACT-003', type: 'adjustment', description: 'Stock count adjustment - Bin A-12',
        quantity: -5, timestamp: new Date(Date.now() - 90 * 60000), user: 'Peter Omondi', status: 'completed'
      },
      {
        id: 'ACT-004', type: 'transfer', description: 'Transfer to Secondary Warehouse',
        quantity: 100, timestamp: new Date(Date.now() - 120 * 60000), user: 'Jane Akinyi', status: 'pending'
      },
      {
        id: 'ACT-005', type: 'inbound', description: 'Returns processing - RMA-0045',
        quantity: 12, timestamp: new Date(Date.now() - 180 * 60000), user: 'David Mwangi', status: 'completed'
      }
    ];

    // Mock Warehouse Locations
    this.warehouseLocations = [
      { id: 'WH-001', name: 'Main Warehouse', capacity: 10000, used: 7845, items: 8234 },
      { id: 'WH-002', name: 'Secondary Warehouse', capacity: 5000, used: 3200, items: 4521 },
      { id: 'WH-003', name: 'Cold Storage', capacity: 2000, used: 1890, items: 2156 },
      { id: 'WH-004', name: 'Overflow Storage', capacity: 3000, used: 450, items: 936 }
    ];

    // Mock Low Stock Alerts
    this.lowStockAlerts = [
      { itemCode: 'ELC-001', itemName: 'USB-C Cables (1m)', currentStock: 15, reorderLevel: 50, supplier: 'TechCorp Ltd' },
      { itemCode: 'OFF-024', itemName: 'A4 Paper Reams', currentStock: 45, reorderLevel: 100, supplier: 'Office Supplies Kenya' },
      { itemCode: 'IT-089', itemName: 'Wireless Mouse', currentStock: 8, reorderLevel: 25, supplier: 'TechCorp Ltd' },
      { itemCode: 'CON-156', itemName: 'Hand Sanitizer 500ml', currentStock: 23, reorderLevel: 50, supplier: 'CleanPro Supplies' },
      { itemCode: 'ELC-045', itemName: 'HDMI Cables (2m)', currentStock: 12, reorderLevel: 30, supplier: 'TechCorp Ltd' }
    ];
  }

  // Actions
  refreshData(): void {
    this.loadDashboardData();
  }

  onTimeRangeChange(range: string): void {
    this.selectedTimeRange = range;
    this.loadDashboardData();
  }

  onWarehouseChange(warehouse: string): void {
    this.selectedWarehouse = warehouse;
    this.loadDashboardData();
  }

  viewAllActivities(): void {
    console.log('Navigate to activities');
  }

  viewAllAlerts(): void {
    console.log('Navigate to alerts');
  }

  createReorderRequest(alert: LowStockAlert): void {
    console.log('Create reorder request for:', alert.itemCode);
  }

  viewItemDetails(itemCode: string): void {
    console.log('View item details:', itemCode);
  }

  exportReport(): void {
    console.log('Export warehouse report');
  }

  // Quick Actions
  receiveGoods(): void { console.log('Navigate to receive goods'); }
  shipOrder(): void { console.log('Navigate to ship order'); }
  stockCount(): void { console.log('Navigate to stock count'); }
  transferStock(): void { console.log('Navigate to transfer'); }
  processReturns(): void { console.log('Navigate to returns'); }
  scanItem(): void { console.log('Open scanner'); }

  // Utilities
  getCapacityPercentage(location: WarehouseLocation): number {
    return Math.round((location.used / location.capacity) * 100);
  }

  getCapacityClass(location: WarehouseLocation): string {
    const percentage = this.getCapacityPercentage(location);
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'normal';
  }

  getActivityIcon(type: string): string {
    const icons: Record<string, string> = {
      'inbound': 'inventory', 'outbound': 'local_shipping',
      'adjustment': 'tune', 'transfer': 'sync_alt'
    };
    return icons[type] || 'inventory_2';
  }

  getActivityClass(type: string): string {
    return `activity-${type}`;
  }

  getStatusClass(status: string): string {
    return `status-${status}`;
  }

  getTrendIcon(trend: string): string {
    const icons: Record<string, string> = {
      'up': 'trending_up', 'down': 'trending_down', 'stable': 'trending_flat'
    };
    return icons[trend] || 'trending_flat';
  }

  getTrendClass(trend: string): string {
    return `trend-${trend}`;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency', currency: 'KES',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value);
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-KE').format(value);
  }

  formatTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }
}
